import React from "react";
import { motion } from "framer-motion";
import { Bot, Cloud, Code, Server, Database, Terminal, Award } from "lucide-react";
import {
  SiReact,
  SiNextdotjs,
  SiTypescript,
  SiTailwindcss,
  SiJavascript,
  SiNodedotjs,
  SiExpress,
  SiPython,
  SiCplusplus,
  SiMongodb,
  SiPostgresql,
  SiMysql,
  SiSqlite,
  SiFirebase,
  SiDocker,
  SiGit,
  SiVercel,
  SiPostman,
  SiLinux,
  SiSalesforce,
  SiShopify,
  SiOpenai,
  SiLangchain,
  SiZapier,
  SiN8N,
  SiHubspot,
  SiHtml5
} from "react-icons/si";

const Skills = () => {
  const skillCategories = [
    {
      title: "AI & Automation",
      icon: <Bot className="w-5 h-5 text-purple-400" />,
      color: "border-purple-500/20 hover:border-purple-500/50 hover:shadow-purple-500/10",
      skills: [
        { name: "OpenAI API", icon: <SiOpenai className="text-emerald-500 w-4 h-4" /> },
        { name: "Gemini API", icon: <Bot className="text-blue-400 w-4 h-4" /> },
        { name: "LangChain", icon: <SiLangchain className="text-cyan-400 w-4 h-4" /> },
        { name: "Agentforce", icon: <Bot className="text-indigo-400 w-4 h-4" /> },
        { name: "RAG Systems", icon: <Database className="text-pink-400 w-4 h-4" /> },
        { name: "Function Calling", icon: <Code className="text-teal-400 w-4 h-4" /> },
        { name: "n8n Workflows", icon: <SiN8N className="text-orange-500 w-4 h-4" /> },
        { name: "Zapier Automations", icon: <SiZapier className="text-orange-600 w-4 h-4" /> }
      ]
    },
    {
      title: "Salesforce & Shopify Clouds",
      icon: <Cloud className="w-5 h-5 text-sky-400" />,
      color: "border-sky-500/20 hover:border-sky-500/50 hover:shadow-sky-500/10",
      skills: [
        { name: "Apex & LWC (PD1)", icon: <SiSalesforce className="text-cyan-500 w-4 h-4" /> },
        { name: "Salesforce Flows / APIs", icon: <SiSalesforce className="text-sky-500 w-4 h-4" /> },
        { name: "Shopify Custom Liquid", icon: <SiShopify className="text-emerald-500 w-4 h-4" /> },
        { name: "Shopify GraphQL Admin", icon: <SiShopify className="text-emerald-600 w-4 h-4" /> },
        { name: "Shopify Apps (Preact/Node)", icon: <SiShopify className="text-green-500 w-4 h-4" /> },
        { name: "HubSpot Integrations", icon: <SiHubspot className="text-orange-500 w-4 h-4" /> }
      ]
    },
    {
      title: "Frontend Engineering",
      icon: <Code className="w-5 h-5 text-cyan-400" />,
      color: "border-cyan-500/20 hover:border-cyan-500/50 hover:shadow-cyan-500/10",
      skills: [
        { name: "React", icon: <SiReact className="text-cyan-400 w-4 h-4" /> },
        { name: "Next.js", icon: <SiNextdotjs className="text-white w-4 h-4" /> },
        { name: "TypeScript", icon: <SiTypescript className="text-blue-500 w-4 h-4" /> },
        { name: "TailwindCSS", icon: <SiTailwindcss className="text-sky-400 w-4 h-4" /> },
        { name: "JavaScript", icon: <SiJavascript className="text-yellow-400 w-4 h-4" /> },
        { name: "HTML5/CSS3", icon: <SiHtml5 className="text-orange-500 w-4 h-4" /> }
      ]
    },
    {
      title: "Backend & Systems",
      icon: <Server className="w-5 h-5 text-emerald-400" />,
      color: "border-emerald-500/20 hover:border-emerald-500/50 hover:shadow-emerald-500/10",
      skills: [
        { name: "Node.js", icon: <SiNodedotjs className="text-green-500 w-4 h-4" /> },
        { name: "Express", icon: <SiExpress className="text-gray-300 w-4 h-4" /> },
        { name: "Python / Flask", icon: <SiPython className="text-yellow-400 w-4 h-4" /> },
        { name: "C++ (DSA)", icon: <SiCplusplus className="text-blue-500 w-4 h-4" /> },
        { name: "Java (OOP)", icon: <Terminal className="text-amber-500 w-4 h-4" /> }
      ]
    },
    {
      title: "Databases & Storage",
      icon: <Database className="w-5 h-5 text-pink-400" />,
      color: "border-pink-500/20 hover:border-pink-500/50 hover:shadow-pink-500/10",
      skills: [
        { name: "MongoDB", icon: <SiMongodb className="text-green-400 w-4 h-4" /> },
        { name: "PostgreSQL", icon: <SiPostgresql className="text-sky-400 w-4 h-4" /> },
        { name: "MySQL", icon: <SiMysql className="text-blue-500 w-4 h-4" /> },
        { name: "SQLite", icon: <SiSqlite className="text-sky-500 w-4 h-4" /> },
        { name: "Firebase Store", icon: <SiFirebase className="text-yellow-500 w-4 h-4" /> }
      ]
    },
    {
      title: "DevOps & Tooling",
      icon: <Award className="w-5 h-5 text-teal-400" />,
      color: "border-teal-500/20 hover:border-teal-500/50 hover:shadow-teal-500/10",
      skills: [
        { name: "Docker", icon: <SiDocker className="text-blue-400 w-4 h-4" /> },
        { name: "Git & GitHub CI", icon: <SiGit className="text-orange-500 w-4 h-4" /> },
        { name: "Vercel / Hosting", icon: <SiVercel className="text-white w-4 h-4" /> },
        { name: "Postman", icon: <SiPostman className="text-orange-500 w-4 h-4" /> },
        { name: "Linux / Bash", icon: <SiLinux className="text-gray-400 w-4 h-4" /> }
      ]
    }
  ];

  return (
    <section id="knowledge" className="relative py-24 px-6 text-white max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs tracking-[0.35em] text-white/60 uppercase">
          Technical Arsenal
        </p>
        <h2 className="mt-3 text-4xl md:text-5xl font-bold leading-tight">
          Structured <span className="bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">Capabilities</span>
        </h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto text-sm md:text-base">
          Categorized skillset showing full-stack execution, advanced CRM systems engineering, and modern AI automation integrations.
        </p>
      </div>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {skillCategories.map((category, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.05 }}
            className={`bg-white/5 border backdrop-blur-xl p-6 rounded-2xl shadow-xl transition-all duration-300 flex flex-col justify-between ${category.color}`}
          >
            <div>
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6 pb-3 border-b border-white/10">
                <span className="p-2 bg-white/5 rounded-lg border border-white/10">
                  {category.icon}
                </span>
                <h3 className="text-lg font-bold text-white tracking-wide">{category.title}</h3>
              </div>

              {/* Skills Grid inside category */}
              <div className="flex flex-wrap gap-2.5">
                {category.skills.map((skill, sIdx) => (
                  <div
                    key={sIdx}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 border border-white/5 shadow-inner transition hover:bg-white/10 hover:scale-[1.03] duration-200"
                  >
                    {skill.icon}
                    <span className="text-xs font-semibold text-gray-200">{skill.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Skills;
